/**
 * Cache Store — read/write `.claude/cache/` directory.
 *
 * Stores navigation-index.json and metadata for graph cache invalidation.
 * Uses atomic write (write-to-temp + rename) for concurrent-access safety.
 */

import { readFile, writeFile, rename, stat, mkdir } from "node:fs/promises";
import { join, dirname } from "node:path";
import { randomBytes } from "node:crypto";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CacheEntry<T = unknown> {
  data: T;
  /** ISO timestamp of when the cache was written. */
  writtenAt: string;
  /** Hash of source inputs used for invalidation. */
  sourceHash: string;
}

export interface CacheStoreOptions {
  /** Absolute path to the `.claude/cache/` directory. */
  cacheDir: string;
}

// ---------------------------------------------------------------------------
// Cache Store
// ---------------------------------------------------------------------------

export interface CacheStore {
  read<T = unknown>(key: string): Promise<CacheEntry<T> | null>;
  write<T = unknown>(key: string, data: T, sourceHash: string): Promise<void>;
  /**
   * Check if a cache entry is fresh by comparing its sourceHash.
   * Returns the cached data if fresh, null if stale or missing.
   */
  readIfFresh<T = unknown>(key: string, currentHash: string): Promise<T | null>;
  /** Get the mtime of a file (for invalidation checks). */
  getFileMtime(filePath: string): Promise<number | null>;
}

export function createCacheStore(options: CacheStoreOptions): CacheStore {
  const { cacheDir } = options;

  function cacheFilePath(key: string): string {
    return join(cacheDir, `${key}.json`);
  }

  async function ensureDir(): Promise<void> {
    await mkdir(cacheDir, { recursive: true });
  }

  const store: CacheStore = {
    async read<T = unknown>(key: string): Promise<CacheEntry<T> | null> {
      try {
        const raw = await readFile(cacheFilePath(key), "utf-8");
        return JSON.parse(raw) as CacheEntry<T>;
      } catch {
        return null;
      }
    },

    async write<T = unknown>(key: string, data: T, sourceHash: string): Promise<void> {
      await ensureDir();
      const entry: CacheEntry<T> = {
        data,
        writtenAt: new Date().toISOString(),
        sourceHash,
      };
      const content = JSON.stringify(entry, null, 2);
      const target = cacheFilePath(key);
      const tmpPath = `${target}.${randomBytes(4).toString("hex")}.tmp`;

      // Atomic write: write to temp file then rename
      await writeFile(tmpPath, content, "utf-8");
      try {
        await rename(tmpPath, target);
      } catch {
        // Fallback: direct write if rename fails (cross-device)
        await writeFile(target, content, "utf-8");
      }
    },

    async readIfFresh<T = unknown>(key: string, currentHash: string): Promise<T | null> {
      const entry = await store.read<T>(key);
      if (!entry) return null;
      if (entry.sourceHash !== currentHash) return null;
      return entry.data;
    },

    async getFileMtime(filePath: string): Promise<number | null> {
      try {
        const s = await stat(filePath);
        return s.mtimeMs;
      } catch {
        return null;
      }
    },
  };

  return store;
}

/**
 * Compute a simple hash string from an array of file mtimes and content strings.
 * Not cryptographic — used only for cache invalidation.
 */
export function computeSourceHash(inputs: Array<string | number>): string {
  return inputs.map(String).join(":");
}
